import { onUnmounted, ref, unref, watch } from "vue";
import { computeRemainingSetTime, countFittingSongs } from "@/utils/setlistCalculator";

/**
 * Live "songs that still fit" ticker for the set-end line.
 *
 * Reads the set clock on an interval and whenever `source` changes, but only
 * writes liveSlots / liveStatus when the value actually changes so a 1s tick
 * does not rebuild ranked lists that depend on these refs.
 *
 * `source` is a computed/ref (or a getter) of:
 *   { setDurationMinutes, setStartedAt, rankedSongs, upNextSong, currentSong,
 *     currentSongStartedAt, pauseTimeSec, defaultSongTimeSec, songLengthMap }
 *
 * The ticker itself is mirrored in web/concerts/src/composables/useSetEndSlots.js.
 * Concerts wraps it in useLiveSetEndLine; admin applies the pin controls itself.
 */
export function useSetEndSlots(source, { intervalMs = 1000 } = {}) {
  const liveSlots = ref(null);
  const liveStatus = ref("inactive");
  let timer = null;

  const readSource = () => {
    const value = unref(source);
    return typeof value === "function" ? value() : value;
  };

  const assign = (slots, status) => {
    if (slots !== liveSlots.value) liveSlots.value = slots;
    if (status !== liveStatus.value) liveStatus.value = status;
  };

  const tick = () => {
    const s = readSource() || {};
    if (!s.setDurationMinutes || s.setDurationMinutes <= 0 || !s.setStartedAt) {
      assign(null, "inactive");
      return;
    }
    const remainingSec = computeRemainingSetTime(s.setDurationMinutes, s.setStartedAt);
    const { slots, status } = countFittingSongs({
      remainingSec,
      rankedSongs: s.rankedSongs ?? [],
      upNextSong: s.upNextSong ?? null,
      pauseTimeSec: s.pauseTimeSec ?? 30,
      defaultSongTimeSec: s.defaultSongTimeSec ?? 240,
      songLengthMap: s.songLengthMap ?? {},
      currentSong: s.currentSong ?? null,
      currentSongStartedAt: s.currentSongStartedAt ?? null
    });
    assign(status === "inactive" ? null : slots, status);
  };

  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = () => {
    stop();
    tick();
    timer = setInterval(tick, intervalMs);
  };

  watch(source, () => tick());
  onUnmounted(stop);

  return { liveSlots, liveStatus, start, stop };
}
